// FORMAT PHONE OFFERS
// ============================
// The phone object runs through this function to determine the highest available PCO points and the associated lowest upfront cost
// If there are no PCO points offers, it just finds the lowest cost
// { phone } object - a single phone from CMS with all the phone details
// { province } string - user selected province to filter available offers through
// ============================
const formatPhoneOffers = (phone, province) => {
  let lowestPrice;
  let highestPoints;

  // Get the lowest between return and keep upfrontPrice.
  const getLowestUpfrontPriceForPhoneVariant = (phoneVariant) => {
    if (Number.isNaN(Number.parseFloat(phoneVariant?.carrierUpfrontPriceReturn))) {
      return phoneVariant?.carrierUpfrontPrice;
    }
    if (phoneVariant?.carrierUpfrontPrice === phoneVariant?.carrierUpfrontPriceReturn) {
      return phone?.carrierUpfrontPriceReturn;
    }
    return Math.min(
      Number(phoneVariant?.carrierUpfrontPrice),
      Number(phoneVariant?.carrierUpfrontPriceReturn),
    );
  };
  // Filter active phone variants to only include ones available in users province
  // Sort by carrier orderOnPage value
  const availableOffers = phone.phoneVariants
    .filter((variant) => variant.isPhoneVariantActive === true)
    .filter((variant) => variant.phoneCarrier.provincialAvailability.includes(province))
    .sort((offerA, offerB) => offerA.phoneCarrier.orderOnPage - offerB.phoneCarrier.orderOnPage);
  phone.availableOffers = availableOffers;

  // Find offers with points and sort by highest points value
  const offersWithPoints = availableOffers
    .filter((offer) => offer.pcoPoints > 0)
    .sort((offerA, offerB) => offerB.pcoPoints - offerA.pcoPoints);

  // Find offers with no points and sort by lowest price
  const offersWithNoPoints = availableOffers
    .filter((offer) => offer.pcoPoints === 0)
    .sort((offerA, offerB) => getLowestUpfrontPriceForPhoneVariant(offerA)
      - getLowestUpfrontPriceForPhoneVariant(offerB));

  // If there are offers with points, find the highest points and lowest associated price with those points
  if (offersWithPoints.length > 0) {
    const highestPointsTiedOffers = offersWithPoints
      .filter((offer) => offer.pcoPoints === offersWithPoints[0].pcoPoints)
      .sort((offerA, offerB) => getLowestUpfrontPriceForPhoneVariant(offerA)
      - getLowestUpfrontPriceForPhoneVariant(offerB));
    lowestPrice = highestPointsTiedOffers[0].carrierUpfrontPrice;
    highestPoints = highestPointsTiedOffers[0].pcoPoints;
  } else if (offersWithNoPoints.length > 0) {
    // If there are no offers with points, find the lowest price and assign 0 points
    lowestPrice = getLowestUpfrontPriceForPhoneVariant(offersWithNoPoints[0]);
    highestPoints = 0;
  }

  // Assign two winning values to phone object
  phone.lowestPrice = lowestPrice;
  phone.highestPoints = highestPoints;

  // Add OEM displayOrder from any variant for easy access
  phone.oemDisplayOrder = phone.phoneVariants[0].phoneBrand.oemDisplayOrder;

  // Add array of available colours on offers for easy access
  const offeredColours = phone.availableOffers.map(
    (offer) => offer.phoneColour,
  );
  phone.availableColours = [
    ...new Map(
      offeredColours.map((colour) => [colour.cmsName, colour]),
    ).values(),
  ];

  // Add array of available storage sizes for easy access
  phone.availableSizes = phone.availableOffers
    .map(({ memory }) => memory)
    // Remove duplicates
    .filter((size, index, arr) => arr.indexOf(size) === index)
    // Sort lowest to highest
    .sort(
      (a, b) => {
        const formattedA = Number(a.replace('TB', '000GB').replace('GB', '000'));
        const formattedB = Number(b.replace('TB', '000GB').replace('GB', '000'));
        return formattedA - formattedB;
      },
    );
  return phone;
};

export default formatPhoneOffers;
